import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Typography, Box, IconButton, useTheme } from "@mui/material";
import { DeveloperTrackItem } from "@/modules/common/types";
 import { useQuery } from "@tanstack/react-query";
import { getDeveloperUsers } from "@/modules/leads/api/index.v2";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import { queryCommonFlags } from "@/api/constants";
import { Trash2,   PlusIcon } from "lucide-react";
import { UserV2 } from "@/modules/user/dtos/index.v2";
import { Developer } from "@/modules/common/dtos/index.v2";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
 
interface TrackItemProps {
  track?: DeveloperTrackItem;
  index?: number;
  developers?: any[];
  isLoadingDevelopers?: boolean;
  onSearchDevelopers?: (value: string) => void;
  onDelete?: (id: string) => void;
  onChange?: (id: string, developerUId: string, developerUserUId: string, developerName?: string, developerLogoPath?: string, agentName?: string) => void;
  isAddButton?: boolean;
  onAdd?: () => void;
  showValidation?: boolean;
}

const TrackItem: React.FC<TrackItemProps> = ({
  track,
  index,
  developers = [],
  isLoadingDevelopers = false,
  onSearchDevelopers = () => {},
  onDelete = () => {},
  onChange = () => {},
  isAddButton = false,
  onAdd = () => {},
  showValidation = false,
}) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const [selectedDeveloperUId, setSelectedDeveloperUId] = useState(track?.developerUId || "");
  const [selectedDeveloperUserUId, setSelectedDeveloperUserUId] = useState(track?.developerUserUId || "");
  const [searchUsers, setSearchUsers] = useState("");
  
  // Track previous values to prevent unnecessary onChange calls
  const prevValuesRef = React.useRef({ 
    developerUId: track?.developerUId || "", 
    userUId: track?.developerUserUId || "" 
  });
  
  const { data: usersData, isFetching: isFetchingUsers } = useQuery({
    queryKey: ['developer-users', selectedDeveloperUId, searchUsers],
    queryFn: () => getDeveloperUsers(selectedDeveloperUId, { excludeBlocked: true, ...(searchUsers?.trim() !== "" ? { search: searchUsers } : {}) }),
    ...queryCommonFlags,
    enabled: !!selectedDeveloperUId && !isAddButton,
   });

  const users = useMemo(() => usersData?.data?.results || [], [usersData]);

 

   useEffect(() => {
    if (!isAddButton && track) {
      // Only call onChange if values actually changed
      const hasChanged = 
        prevValuesRef.current.developerUId !== selectedDeveloperUId ||
        prevValuesRef.current.userUId !== selectedDeveloperUserUId;
      
      if (hasChanged) {
        onChange(
          track.id, 
          selectedDeveloperUId, 
          selectedDeveloperUserUId,
        );
        
        // Update ref with new values
        prevValuesRef.current = {
          developerUId: selectedDeveloperUId,
          userUId: selectedDeveloperUserUId
        };
      }
    }
  }, [selectedDeveloperUId, selectedDeveloperUserUId, track?.id, isAddButton, onChange    ]);

  const handleDeveloperChange = useCallback((val: string) => {
    setSelectedDeveloperUId(val);
    setSelectedDeveloperUserUId(''); // Reset user when developer changes
    setSearchUsers(''); // Clear user search when developer changes
  }, []);

  const handleUserChange = useCallback((val: string) => {
    setSelectedDeveloperUserUId(val);
  }, []);

  // Render "Add Track" button variant
  if (isAddButton) {
    return (
      <Box
        onClick={onAdd}
        sx={{
          cursor: 'pointer',
          display: 'inline-flex',
          maxWidth: 'fit-content',
          alignItems: 'center',
          gap: 0.5,
          transition: 'opacity 0.2s ease-in-out',
          '&:hover': {
            opacity: 0.7,
          },
          userSelect: 'none',
        }}
      >
        <PlusIcon color={theme.nawy.colors.semantic.layer.accent} size={15} />
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.semibold,
            color: theme.nawy.colors.semantic.layer.accent,
          }}
        >
          Add track
        </Typography>
      </Box>
    );
  }

  // Check validation errors (agent is optional)
  const hasDeveloperError = showValidation && !selectedDeveloperUId;

  // Render regular track item
  return (
    <Box sx={{
      padding: 2,
       borderRadius: 2,
       backgroundColor: theme.nawy.colors.primitives.navy[50],
       border: hasDeveloperError ? `2px solid ${theme.nawy.colors.semantic.text.extension.negative}` : 'none',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{                ...theme.nawy.typography.english.variants.body.md.semibold}}>Track {(index || 0) + 1}</Typography>
        <IconButton onClick={() => track && onDelete(track.id)} size="small" >
          <Trash2 size={16} color={theme.nawy.colors.semantic.text.primary} />
        </IconButton>
      </Box>
 
      <Box sx={{ 
        display: 'flex', 
        gap: 2,
        padding: 2,
        borderRadius: 2,
        backgroundColor: theme.nawy.colors.primitives.white[100],
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        <Box sx={{ width: '100%' , display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{                ...theme.nawy.typography.english.variants.body.xs.regular}}>Developer</Typography>
          <SingleSelectSearch
            value={selectedDeveloperUId}
            label=""
            options={developers.map((dev: Developer) => ({ label: dev.name, value: dev.uid }))}
            onValueChange={(val) => handleDeveloperChange(val as string)}
            onSearchValueChange={onSearchDevelopers}
            isLoading={isLoadingDevelopers}
            error={hasDeveloperError ? "Developer is required" : undefined}
          />
        </Box>
        <Box sx={{ width: '100%' , display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{                ...theme.nawy.typography.english.variants.body.xs.regular}}>Agent</Typography>
          <SingleSelectSearch
            key={`agent-${selectedDeveloperUId}`}
            label=""
            value={selectedDeveloperUserUId}
            options={users.map((user: UserV2) => ({ 
              label: user.name, 
              value: user.uid,
              phoneNumber: user.phoneNumber
            }))}
            onValueChange={(val) => handleUserChange(val as string)}
            onSearchValueChange={setSearchUsers}
            disabled={!selectedDeveloperUId}
            isLoading={isFetchingUsers}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TrackItem;
